import api from '@/services/api'

/**
 * Normalize a Compliance Consult notify action into editable workspace state.
 *
 * @param {Object} payload
 * @returns {Object}
 */
export function buildNotificationDraft(payload = {}) {
  const draft = payload.email_draft || {}
  const entityIds = payload.subject_ids || payload.entityIds || payload.entity_ids || []
  const candidateIds = payload.candidate_subject_ids || entityIds

  return {
    entityIds,
    candidateIds,
    candidateTotal: payload.candidate_total || candidateIds.length,
    batchSize: Number(payload.batch_size) || null,
    asOf: payload.as_of || null,
    template: payload.template || draft.template || 'gap_notification',
    subject:
      draft.subject ||
      payload.subject ||
      'Action Required: Compliance Coverage Update Needed',
    customMessage:
      draft.body ||
      payload.assistant_text ||
      payload.customMessage ||
      '',
    recipientsSummary:
      draft.recipients_summary ||
      payload.recipients_summary ||
      [],
    recipientRouteCounts:
      draft.recipient_route_counts ||
      payload.recipient_route_counts ||
      {},
    recipientPreference: payload.recipient_preference || 'agent',
    ccSubcontractor: payload.cc_subcontractor !== false,
    aiGenerated: Boolean(draft.body || payload.assistant_text),
    generationTime: null,
  }
}

/**
 * Resolve the real To addresses for a set of subjects without sending or
 * creating decisions. Used to fill the draft "To:" field so an operator can
 * see the address — or that there isn't one — before sending.
 *
 * @param {Array<number|string>} subjectIds
 * @param {string} recipientPreference 'agent' | 'insured'
 * @returns {Promise<{recipients: Array<Object>, recipientRouteCounts: Object}>}
 */
export async function fetchNotificationRecipients(
  subjectIds,
  recipientPreference = 'agent',
  ccSubcontractor = true
) {
  const response = await api.post('/compliance-health/notification-recipients/', {
    subject_ids: subjectIds,
    recipient_preference: recipientPreference,
    cc_subcontractor: ccSubcontractor,
  })

  return {
    recipients: response.data?.recipients || [],
    recipientRouteCounts: response.data?.recipient_route_counts || {},
  }
}

/**
 * Pick the next batch to email from a matched set, without sending.
 * Stores emailed inside ``skipRecentDays`` are skipped, so calling this
 * again after a send returns the following batch.
 *
 * @param {Object} options
 * @param {Array<number|string>} options.candidateIds Ordered matched subjects
 * @param {number} options.batchSize Stores per batch (server max 200)
 * @param {number} options.skipRecentDays 0 to include recently emailed stores
 * @param {string} options.recipientPreference 'agent' | 'insured'
 * @param {boolean} options.ccSubcontractor
 * @returns {Promise<Object>} subject_ids, recipients, counts, daily_remaining
 */
export async function fetchNotificationBatch(options) {
  const response = await api.post('/compliance-health/notification-batch/', {
    candidate_subject_ids: options.candidateIds,
    batch_size: options.batchSize,
    skip_recent_days: options.skipRecentDays,
    recipient_preference: options.recipientPreference || 'agent',
    cc_subcontractor: options.ccSubcontractor !== false,
  })
  return response.data || {}
}

/**
 * Regenerate a compliance outreach cover note for selected subjects.
 *
 * @param {Object} options
 * @returns {Promise<Object>}
 */
export async function generateNotificationDraft(options) {
  const response = await api.post('/compliance-health/email/preview/', {
    entity_ids: options.entityIds,
    email_type: 'compliance_notification',
    tone: options.tone,
    urgency: options.urgency,
    custom_instructions: options.instructions || undefined,
  })

  if (!response.data?.success) {
    throw new Error(response.data?.error || 'Failed to generate email')
  }

  return response.data
}

/**
 * Queue personalized compliance notifications after operator review.
 *
 * @param {Object} draft
 * @param {number|string|null} projectId
 * @returns {Promise<Object>}
 */
export async function sendComplianceNotifications(draft, projectId = null) {
  const response = await api.post('/compliance-health/agent/notify/', {
    subject_ids: draft.entityIds,
    entity_ids: draft.entityIds,
    template: draft.template,
    message: draft.customMessage,
    email_subject: draft.subject,
    email_body: draft.customMessage,
    project_id: projectId,
    recipient_preference: draft.recipientPreference || 'agent',
    cc_subcontractor: draft.ccSubcontractor !== false,
  })
  return response.data
}
